import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { Spin } from 'lego-on-react';
import { isEqual } from 'lodash';
import * as React from 'react';

import { DEFAULT_SITELINKS_COUNT } from '../../constants';
import { Bem } from '../../service/Bem/Bem';
import { SiteLinkInfo } from '../../typings/banners';
import { protocolMenuItems, ProtocolValue } from '../../typings/bannersEditState';
import { Alert, AlertSize, AlertType } from '../Alert/Alert';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { DirectFormInput } from '../DirectFormInput/DirectFormInput';
import { ErrorText, ErrorTextTheme } from '../ErrorText/ErrorText';
import { InputFieldType } from '../InputField/InputField';
import { SelectDecorator, SelectItem } from '../SelectDecorator/SelectDecorator';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';
import { SiteLinkTitleCounter } from '../SiteLinkTitleCounter/SiteLinkTitleCounter';
import { TurbolandingCompact } from '../TurbolandingCompact/TurbolandingCompact';

import './SiteLink.css';
import * as keyset from './SiteLink.i18n';

const i18n = i18nFactory(keyset);

export enum SiteLinkField {
    title = 'title',
    descr = 'descr',
    link = 'link',
    turbo = 'turbo',
    protocol = 'protocol'
}

export type SiteLinkFieldData = string;

export interface SiteLinkProps {
    siteLinkInfo: SiteLinkInfo;
    isDescriptionEnabled?: boolean;
    index: number;
    onChange: (index: number, field: SiteLinkField, value: SiteLinkFieldData | undefined) => void;
    onOpenCanvas: (index: number) => void;
    onProtocolChange: (index: number, value: ProtocolValue) => void;
    onRemoveSiteLink: (index: number) => void;
    isHrefHidden: boolean;
}

const protocolMenuItemsWithDifferent: SelectItem[] = [
    {
        val: ProtocolValue.different,
        text: i18n('different-protocols')
    },
    ...protocolMenuItems
];

const BLOCK = 'site-link';

const turboPageError = [
    {
        text: i18n('site-link-turbo-error')
    }
];

export class SiteLink extends React.Component<SiteLinkProps> {
    public static defaultProps = {
        protocolValue: ProtocolValue.https
    };

    shouldComponentUpdate(nextProps: SiteLinkProps) {
        return (
            !isEqual(this.props.siteLinkInfo, nextProps.siteLinkInfo) ||
            this.props.isDescriptionEnabled !== nextProps.isDescriptionEnabled
        );
    }

    render() {
        const { isDescriptionEnabled, index, isHrefHidden } = this.props;

        return (
            <div className={Bem.cls(BLOCK)}>
                <div
                    className={Bem.cls(BLOCK, 'fields', {
                        short: index >= DEFAULT_SITELINKS_COUNT
                    })}
                >
                    {this.getTitleField()}
                    {isDescriptionEnabled && this.getDescrField()}
                    {!isHrefHidden && this.getLinkField()}
                    {this.getTurboField()}
                </div>
            </div>
        );
    }

    private onRemoveSiteLink = () => {
        const { index } = this.props;

        this.props.onRemoveSiteLink(index);
    };

    private getTitleField() {
        const {
            siteLinkInfo: {
                title: {
                    countFirstBlock,
                    countSecondBlock,
                    limit,
                    text,
                    valid,
                    errors = [],
                    isMassActionValuesDifferent
                },
                isPreparedSiteLinkFromMassAction
            },
            index
        } = this.props;

        return (
            <div
                className={Bem.cls(BLOCK, 'field-wrapper', {
                    title: true
                })}
            >
                <p className={Bem.cls(BLOCK, 'field-title')}>{i18n('site-link-title')}</p>
                <div className={Bem.cls(BLOCK, 'field-title-input-wrapper')}>
                    <DirectFormInput
                        cls={Bem.cls(BLOCK, 'field-input')}
                        theme={'normal'}
                        size={'n'}
                        text={text}
                        errorText={<ErrorText theme={ErrorTextTheme.tooltip} errors={errors} />}
                        count={
                            <SiteLinkTitleCounter
                                limit={limit}
                                availableFirstBlock={countFirstBlock}
                                availableSecondBlock={countSecondBlock}
                            />
                        }
                        placeholder={
                            isMassActionValuesDifferent
                                ? i18n('site-link-different-values-placeholder')
                                : i18n('site-link-placeholder')
                        }
                        onChange={this.onChangeTitleField}
                        inputType={InputFieldType.TEXT}
                        valid={valid}
                        hasClear={true}
                        autoFocus={index === 0}
                    />
                    {index >= DEFAULT_SITELINKS_COUNT && !isPreparedSiteLinkFromMassAction && (
                        <ButtonDecorator
                            cls={Bem.cls(BLOCK, 'remove-button')}
                            theme={'clear'}
                            size={'n'}
                            width={'max'}
                            onClick={this.onRemoveSiteLink}
                        >
                            <span className={Bem.cls(BLOCK, 'remove-button-text')}>
                                <Shape2
                                    width={16}
                                    height={16}
                                    icon={Shape2Icon.trashbin}
                                    cls={Bem.cls(BLOCK, 'remove-button-icon')}
                                />
                            </span>
                        </ButtonDecorator>
                    )}
                </div>
            </div>
        );
    }

    private getDescrField() {
        const {
            siteLinkInfo: {
                descr: { count, text, valid, errors = [], isMassActionValuesDifferent }
            }
        } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'field-wrapper')}>
                <p className={Bem.cls(BLOCK, 'field-title')}>{i18n('site-link-descr')}</p>
                <DirectFormInput
                    theme={'normal'}
                    size={'n'}
                    cls={Bem.cls(BLOCK, 'description')}
                    text={text}
                    errorText={<ErrorText theme={ErrorTextTheme.tooltip} errors={errors} />}
                    count={count}
                    placeholder={
                        isMassActionValuesDifferent
                            ? i18n('site-link-different-values-placeholder')
                            : i18n('site-link-descr')
                    }
                    onChange={this.onChangeDescrField}
                    inputType={InputFieldType.TEXT}
                    valid={valid}
                    hasClear={true}
                />
            </div>
        );
    }

    private getLinkField() {
        const {
            siteLinkInfo: {
                link: { count, text, valid, errors = [], isUrlChecking = false, isMassActionValuesDifferent }
            }
        } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'field-wrapper')}>
                <p className={Bem.cls(BLOCK, 'field-title')}>{i18n('site-link-link')}</p>
                <div className={Bem.cls(BLOCK, 'input-form')}>
                    {this.renderProtocol()}
                    <DirectFormInput
                        cls={Bem.cls(BLOCK, 'input')}
                        theme="normal"
                        size="n"
                        text={text}
                        errorText={<ErrorText theme={ErrorTextTheme.tooltip} errors={errors} />}
                        count={count}
                        placeholder={
                            isMassActionValuesDifferent
                                ? i18n('site-link-different-values-placeholder')
                                : i18n('site-link-placeholder')
                        }
                        onChange={this.onChangeLinkField}
                        inputType={InputFieldType.TEXT}
                        valid={valid}
                        hasClear={true}
                    />
                </div>
                {isUrlChecking && (
                    <div className={Bem.cls(BLOCK, 'loader')}>
                        <Spin size={'xs'} progress={true} />
                    </div>
                )}
            </div>
        );
    }

    private renderProtocol() {
        const {
            siteLinkInfo: { protocol, isMassActionProtocolValuesDifferent }
        } = this.props;

        const protocolItems = isMassActionProtocolValuesDifferent ? protocolMenuItemsWithDifferent : protocolMenuItems;

        return (
            <SelectDecorator
                cls={Bem.cls(BLOCK, 'dropdown', { different: protocol === ProtocolValue.different })}
                size="n"
                theme="normal"
                type="radio"
                view="classic"
                items={protocolItems}
                val={protocol}
                onChange={this.onProtocolChange}
            />
        );
    }

    private getTurboField() {
        const {
            siteLinkInfo: { turbo }
        } = this.props;

        return (
            <>
                <div
                    className={classNames(
                        Bem.cls(BLOCK, 'field-wrapper', { turbo: true }),
                        Bem.cls(BLOCK, 'turbolanding')
                    )}
                >
                    <TurbolandingCompact
                        cls={Bem.cls(BLOCK, 'turbo')}
                        onOpenCanvas={this.onOpenCanvas}
                        onDeleteTurbo={this.onDeleteTurbo}
                        turbolanding={turbo.data}
                        isLoading={turbo.isLoading}
                        isMassActionsDifferentValues={turbo.isMassActionValuesDifferent}
                    />
                </div>
                {this.getTurboError()}
            </>
        );
    }

    private getTurboError = () => {
        const {
            siteLinkInfo: {
                link: { errors = [] }
            },
            isHrefHidden
        } = this.props;

        // DIRECT-106614. Бэк не умеет валидировать отдельно турбу, поэтому валидируем ее в компоненте на основе отсутствия ссылки
        if (isHrefHidden && errors.length) {
            return (
                <Alert type={AlertType.error} size={AlertSize.s}>
                    <ErrorText errors={turboPageError} />
                </Alert>
            );
        }

        return null;
    };

    private onChangeTitleField = (value: string) => {
        const { onChange, index } = this.props;

        onChange(index, SiteLinkField.title, value);
    };

    private onChangeDescrField = (value: string) => {
        const { onChange, index } = this.props;

        onChange(index, SiteLinkField.descr, value);
    };

    private onChangeLinkField = (value: string) => {
        const { onChange, index } = this.props;

        onChange(index, SiteLinkField.link, value);
    };

    private onOpenCanvas = () => {
        const { index, onOpenCanvas } = this.props;

        onOpenCanvas(index);
    };

    private onDeleteTurbo = () => {
        const { onChange, index } = this.props;

        onChange(index, SiteLinkField.turbo, undefined);
    };

    private onProtocolChange = (value: Array<ProtocolValue>): void => {
        const {
            index,
            onProtocolChange,
            siteLinkInfo: { protocol }
        } = this.props;

        if (!value || protocol === value[0]) {
            return;
        }

        onProtocolChange(index, value[0]);
    };
}
